import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "./tournament.css";

const getEliminationRoundCount = (participantCount) =>
  Math.max(1, Math.ceil(Math.log2(participantCount)));

const DEFAULT_PARTICIPANT_COUNT = 8;
const SMART_TOURNAMENT_TYPE = "Smart Tournament";
const PLAYERS_PER_MATCH = 2;

function getSmartTotalRounds(playerCount) {
  if (playerCount <= 2) return 1;
  if (playerCount <= 4) return 2;
  return 6;
}

function getKnockoutTotalRounds(playerCount, ppm) {
  if (playerCount <= 1) return 1;
  let remaining = playerCount;
  let rounds = 0;
  while (remaining > 1) {
    remaining = Math.ceil(remaining / ppm);
    rounds += 1;
  }
  return Math.max(1, rounds);
}

function buildRoundLabelMap(tournamentType, playerCount, ppm, dbMaxRound = 0) {
  const isSmartType = tournamentType === SMART_TOURNAMENT_TYPE;
  let totalRounds;
  let prefix;

  if (isSmartType) {
    if (playerCount === 2) return { 1: "FINAL" };
    if (playerCount === 3) return { 1: "Round 1", 2: "FINAL" };
    if (playerCount === 4) return { 1: "SEMI FINAL", 2: "FINAL" };
    totalRounds = getSmartTotalRounds(playerCount);
    prefix = "Match";
  } else {
    totalRounds = getKnockoutTotalRounds(playerCount, ppm);
    prefix = "Round";
  }

  if (playerCount < 1 && dbMaxRound > 0) {
    totalRounds = Math.max(totalRounds, dbMaxRound);
  }

  const labels = {};
  const prelimCount = Math.max(0, totalRounds - 2);
  for (let i = 1; i <= prelimCount; i += 1) {
    labels[i] = `${prefix} ${i}`;
  }
  if (totalRounds >= 2) labels[totalRounds - 1] = "SEMI FINAL";
  if (totalRounds >= 1) labels[totalRounds] = "FINAL";
  return labels;
}

function getSeatDotColor(rawColor) {
  const normalized = String(rawColor || "").toLowerCase();
  if (normalized === "black") return "#1a1a1a";
  if (normalized === "white") return "#f5f5f5";
  return "#ccc";
}

function TournamentStructure({ updateHeader }) {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [activeRound, setActiveRound] = useState(0);
  const [loading, setLoading] = useState(true);
  const [apiRounds, setApiRounds] = useState([]);
  const [tournamentType, setTournamentType] = useState("knockout");
  const [playerCount, setPlayerCount] = useState(0);

  const isSmart = tournamentType === SMART_TOURNAMENT_TYPE;

  const roundLabelMap = useMemo(() => {
    const dbMaxRound = apiRounds.length;
    const count = playerCount > 0 ? playerCount : DEFAULT_PARTICIPANT_COUNT;
    return buildRoundLabelMap(tournamentType, count, PLAYERS_PER_MATCH, dbMaxRound);
  }, [tournamentType, playerCount, apiRounds.length]);

  const roundCount = useMemo(() => {
    if (!apiRounds?.length) {
      return getEliminationRoundCount(DEFAULT_PARTICIPANT_COUNT);
    }

    if (isSmart) {
      return apiRounds.length;
    }

    let latestIdx = -1;
    for (let i = apiRounds.length - 1; i >= 0; i--) {
      if (apiRounds[i]?.matches?.length) {
        latestIdx = i;
        break;
      }
    }
    if (latestIdx === -1) {
      return getEliminationRoundCount(DEFAULT_PARTICIPANT_COUNT);
    }

    const latestMatchCount = apiRounds[latestIdx].matches.length;
    const remainingRounds = Math.max(
      1,
      Math.ceil(Math.log2(Math.max(1, latestMatchCount))) + 1
    );

    return latestIdx + remainingRounds;
  }, [apiRounds, isSmart]);

  const rounds = useMemo(
    () =>
      Array.from({ length: roundCount }, (_, i) => ({
        roundIndex: i,
        matches: apiRounds[i]?.matches ?? [],
      })),
    [roundCount, apiRounds]
  );

  const defaultRoundIndex = useMemo(() => {
    if (!apiRounds?.length) return 0;

    if (isSmart) {
      for (let i = 0; i < apiRounds.length; i++) {
        const matches = apiRounds[i]?.matches ?? [];
        if (matches.some((m) => m.status === "not_started")) {
          return i;
        }
      }
    }

    for (let i = apiRounds.length - 1; i >= 0; i--) {
      if (apiRounds[i]?.matches?.length) return i;
    }
    return 0;
  }, [apiRounds, isSmart]);

  const participationRole = useMemo(() => {
    if (!userData?.userId || !apiRounds?.length) return "Watcher";
    for (const round of apiRounds) {
      for (const m of round.matches || []) {
        const inMatch = (m.players || []).some(
          (p) => String(p.userid) === String(userData.userId)
        );
        if (inMatch || m.canJoin) return "Player";
      }
    }
    return "Watcher";
  }, [apiRounds, userData]);

  const hasSetDefaultRoundRef = React.useRef(false);
  useEffect(() => {
    if (loading || !apiRounds?.length) return;
    if (!hasSetDefaultRoundRef.current) {
      hasSetDefaultRoundRef.current = true;
      setActiveRound(defaultRoundIndex >= 0 ? defaultRoundIndex : 0);
    }
  }, [loading, apiRounds?.length, defaultRoundIndex]);

  useEffect(() => {
    const stored = sessionStorage.getItem("userData");
    if (stored) setUserData(JSON.parse(stored));
  }, []);

  useEffect(() => {
    if (!userData || !tournamentId) return;
    fetchMatches();

    const pollInterval = setInterval(() => {
      fetchMatches();
    }, 30000);
    return () => clearInterval(pollInterval);
  }, [userData, tournamentId]);

  useEffect(() => {
    updateHeader({ backButtonRedirect: "/rules" });
  }, [updateHeader]);

  const fetchMatches = async () => {
    try {
      const res = await fetch(
        process.env.REACT_APP_FOLDER_LINK + "/api/getMatchesList.php",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            tournamentId,
            userId: userData.userId,
          }),
        }
      );

      const data = await res.json();
      if (
        data.success === false &&
        data.error === "Tournament matches not generated"
      ) {
        setLoading(false);
        navigate("/tournament-page");
        return;
      }
      if (data.tournamentType) {
        setTournamentType(data.tournamentType);
      }
      if (typeof data.playerCount === "number") {
        setPlayerCount(data.playerCount);
      }
      setApiRounds(data.tournaments || []);
    } catch (err) {
      console.error(err);
      Swal.fire("Error", "Failed to load matches", "error");
    } finally {
      setLoading(false);
    }
  };

  const getRoundName = (index) => {
    const total = rounds.length;
    if (total - index === 1) return "FINAL";
    if (total - index === 2) return "SEMI FINAL";
    if (roundLabelMap[index + 1]) return roundLabelMap[index + 1];
    if (!isSmart) return `Round ${index + 1}`;
    return `Match ${index + 1}`;
  };

  const getTbdCount = (roundIndex, totalRounds) => {
    if (isSmart) return 0;
    if (totalRounds - roundIndex === 1) return 1;
    if (totalRounds - roundIndex === 2) return 2;
    return 1;
  };

  const formatPlayerName = (p) => {
    if (!p) return "---";
    if (p.userid === "TBD" || p.name === "TBD") return "---";
    return p.name || "Unknown";
  };

  const formatDateTime = (dateStr) => {
    const d = new Date(dateStr);
    const datePart = d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
    const timePart = d
      .toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      })
      .replace(/\s*(am|pm)$/i, (_, m) => " " + m.toUpperCase());
    return `${datePart} ${timePart}`;
  };

  const goToSpectate = (m) => {
    const params = new URLSearchParams({
      gameId: m.gameId,
      roomCode: m.gameId,
      mode: "multiplayer",
      role: "spectator",
      name: userData?.name || "",
      userId: userData?.userId || "",
    });
    if (tournamentId) params.set("tournamentId", tournamentId);
    if (m.game_type) params.set("gameType", String(m.game_type));
    window.location.href = `/snooker-game?${params.toString()}`;
  };

  const getMatchButton = (m) => {
    const start = new Date(m.datetime);
    const end = new Date(start.getTime() + 10 * 60000);
    const now = new Date();

    // Completed match: everyone (players + non-players) can view the final state
    if (m.status === "completed") {
      if (m.canView) {
        return (
          <button className="view-button-match" onClick={() => goToSpectate(m)}>
            View
          </button>
        );
      }
      return null;
    }

    // Player of this match within the live window → Play button
    if (m.canJoin && now >= start && now <= end) {
      let playerColor = "black";
      if (userData && Array.isArray(m.players)) {
        const me = m.players.find((p) => p.userid === userData.userId);
        if (me?.color) {
          const normalized = String(me.color).toLowerCase();
          if (normalized === "black" || normalized === "white") {
            playerColor = normalized;
          }
        }
      }

      return (
        <button
          className="join-button-match"
          onClick={() => {
            if (!userData) return;
            const params = new URLSearchParams({
              gameId: m.gameId,
              roomCode: m.gameId,
              mode: "multiplayer",
              name: userData.name || "",
              userId: userData.userId || "",
              color: playerColor,
            });
            if (tournamentId) params.set("tournamentId", tournamentId);
            if (m.game_type) params.set("gameType", String(m.game_type));
            window.location.href = `/snooker-game?${params.toString()}`;
          }}
        >
          Play
        </button>
      );
    }

    // Non-player, live window → View (spectate) button
    if (m.canView && now >= start && now <= end) {
      return (
        <button className="view-button-match" onClick={() => goToSpectate(m)}>
          View
        </button>
      );
    }

    if (!m.canJoin) return null;

    if (now > end) {
      return <button className="join-gray">Times Up!</button>;
    }

    return <button className="join-gray">Play</button>;
  };

  return (
    <div className="tournament-wrapper">
      <div className="tournament-title">Tournament Schedule</div>

      <div className="participation-notice">
        You are currently participating as: <strong>{participationRole}</strong>.
        Your match will automatically become active at the scheduled start time shown
        below. If it does not activate exactly at the scheduled time, please refresh the
        match page. If you join after the scheduled start time or after the match has
        ended, you will be marked as a No Show, and only the View button will be
        available. If your opponent does not join, you must remain on the match page
        until the scheduled end of the match. Leaving before the match concludes may
        affect the result. If your opponent fails to join for the entire match duration
        and you remain present until the end, you will be declared the winner.
      </div>

      {loading ? (
        <div style={{ textAlign: "center", color: "#fff" }}>Loading...</div>
      ) : (
        <>
          <div className="tournament-round-container">
            {rounds.map((_, i) => (
              <button
                key={i}
                className={`top-tabs ${activeRound === i ? "tab-active" : ""}`}
                onClick={() => setActiveRound(i)}
              >
                {getRoundName(i)}
              </button>
            ))}
          </div>

          {rounds.map((round, i) =>
            i === activeRound ? (
              <div key={i} className="round-main-container">
                <div className="round-data">{getRoundName(i)}</div>

                <div className="table-matches">
                  <table className="table table-striped">
                    <thead>
                      <tr>
                        <th>Players</th>
                        <th>Outcome</th>
                        <th>Date Time</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(round.matches.length > 0
                        ? round.matches
                        : Array(getTbdCount(i, rounds.length)).fill({ tbd: true })
                      ).map((m, j) =>
                        m.tbd ? (
                          <tr key={j}>
                            <td className="player-head">TBD vs TBD</td>
                            <td className="outcome-head">-</td>
                            <td>-</td>
                            <td>-</td>
                          </tr>
                        ) : (
                          <tr key={j}>
                            <td className="player-head" data-label="Players">
                              {m.players.map((p, idx) => {
                                const isYou = p.userid === userData?.userId;
                                const isWinner =
                                  m.status === "completed" &&
                                  m.winnerId &&
                                  p.userid === m.winnerId;
                                const dotColor = getSeatDotColor(p.color);

                                return (
                                  <div
                                    key={idx}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 6,
                                    }}
                                  >
                                    <span
                                      style={{
                                        display: "inline-block",
                                        width: 10,
                                        height: 10,
                                        borderRadius: "50%",
                                        backgroundColor: dotColor,
                                        border: "1px solid rgba(0,0,0,0.25)",
                                        flexShrink: 0,
                                      }}
                                    />
                                    <span>
                                      {formatPlayerName(p)}
                                      {p.is_bye ? (
                                        <span style={{ color: "#999" }}>
                                          {" "}
                                          (BYE)
                                        </span>
                                      ) : null}
                                      {isYou && (
                                        <strong style={{ color: "green" }}>
                                          {" "}
                                          (You)
                                        </strong>
                                      )}
                                      {isWinner && (
                                        <strong style={{ color: "green" }}>
                                          {" "}
                                          (winner)
                                        </strong>
                                      )}
                                    </span>
                                  </div>
                                );
                              })}
                            </td>

                            <td className="outcome-head" data-label="Outcome">
                              {m.status === "completed" ? m.outcome : "-"}
                            </td>

                            <td data-label="Date Time">
                              {formatDateTime(m.datetime)}
                            </td>

                            <td data-label="Action">{getMatchButton(m)}</td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null
          )}
        </>
      )}

      <img src="/images/key1.png" className="top-left-image" alt="" />
      <img src="/images/key2.png" className="top-right-image" alt="" />
    </div>
  );
}

export default TournamentStructure;
